import React, { useEffect, useState } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import { AlternativePagination } from "../../components/Common/Table";
import "../../global.css";
import AWSS3Uploader from "../../components/Common/S3uploader";
import {
  AddCategory,
  GetCategories,
  DeleteCategory,
  UpdateCategory,
} from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from 'react-toastify';

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const Category = () => {
  document.title = "Category | Susipizza";

  const [modal, setModal] = useState(false);
  const [category, setCategory] = useState("");
  const [image, setImage] = useState(null);
  const [categoryData, setcategoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [buttonText, setButtonText] = useState("Save");
  const [catId, setCatId] = useState("");
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [status, setStatus] = useState("active");
  const [displayOrder, setDisplayOrder] = useState(0);

  const toggle = () => {
    if (modal) {
      setCategory("");
      setImage(null);
      setEditingId(null);
      setButtonText("Save");
      setCatId("");
      setStatus("active");
      setDisplayOrder(0);
    }
    setModal(!modal);
  };

  const handleCategoryNameChange = (e) => {
    const value = e.target.value;
    setCategory(value);
    // Auto-slugify Category ID for new categories
    if (!editingId) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-") // replace non-alphanumeric with hyphen
        .replace(/^-+|-+$/g, ""); // trim leading/trailing hyphens
      setCatId(slug);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setCategory(row.category);
    setImage(row.image);
    setCatId(row.categoryId || row.id);
    setStatus(row.status || "active");
    setDisplayOrder(row.displayOrder ?? 0);
    setButtonText("Update");
    setModal(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (editingId) {
        await UpdateCategory(token, editingId, {
          category: category,
          image: image,
          categoryId: editingId, // Keep same ID on update
          status: status,
          displayOrder: Number(displayOrder) || 0,
        });
        toast.success('Category updated successfully');
      } else {
        await AddCategory(token, {
          category: category,
          image: image,
          categoryId: catId,
          status: status,
          displayOrder: Number(displayOrder) || 0,
        });
        toast.success('Category added successfully');
      }
      toggle();
      getData();
    } catch (err) {
      console.error("Save category error:", err.response?.data || err);
      const validationErrs = err.response?.data?.validationErrors;
      if (validationErrs && Array.isArray(validationErrs)) {
        validationErrs.forEach(e => toast.error(`${e.field}: ${e.message}`));
      } else {
        const errorMsg = err.response?.data?.error?.message || err.response?.data?.error || 'Error occurred while saving category';
        toast.error(errorMsg);
      }
    } finally {
      setCategory("");
      setImage(null);
      setCatId("");
      setEditingId(null);
      setButtonText("Save");
      setStatus("active");
      setDisplayOrder(0);
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteCategory(token, targetId);
      toast.success("Category removed successfully");
      getData();
    } catch (err) {
      console.error(err);
      toast.error("Error occurred while removing category");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const resp = await GetCategories();
      
      setcategoryData(resp.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">SR No.</span>,
      selector: (row, index) => (index + 1).toString(),
    },
    {
      name: <span className="font-weight-bold fs-13">Category ID</span>,
      selector: (row) => row.id,
      width: "220px",
    },

    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      cell: (row) => (
        <img src={row.image} alt={row.category || "Category"} width="50" height="50" style={{ objectFit: "cover", borderRadius: "4px" }} />
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Category</span>,
      selector: (row) => row.category,
      // sortable: true
    },
    {
      name: <span className="font-weight-bold fs-13">Order</span>,
      selector: (row) => row.displayOrder ?? 0,
      width: "90px",
    },
    {
      name: <span className="font-weight-bold fs-13">Status</span>,
      width: "110px",
      cell: (row) => (
        <span className={`badge ${(row.status || "active") === "active" ? "bg-success" : "bg-secondary"}`}>
          {(row.status || "active") === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      width: "110px",
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-icon btn-outline-primary"
            onClick={() => startEdit(row)}
            title="Edit Category"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-icon btn-outline-danger"
            onClick={() => handleDeleteClick(row.id)}
            title="Remove Category"
          >
            <i className="mdi mdi-trash-can-outline"></i>
          </button>
        </div>
      ),
    },
  ];

  

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading}/>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Category" />

          <button type="button" className="btn btn-primary mb-3" onClick={toggle}>
            Add Category
          </button>
          <AlternativePagination columns={columns} data={categoryData} />
        </Container>
      </div>

      {/* Modal */}

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{editingId ? "Edit Category" : "Add Category"}</ModalHeader>
        <ModalBody className="modal-color">
          <FormGroup>
            <Label for="exampleEmail">Image</Label>
            <AWSS3Uploader onUploadComplete={(url) => setImage(url)} folder="categories" />
          </FormGroup>
          <FormGroup>
            <Label for="categoryName">Category Name</Label>
            <Input
              id="categoryName"
              name="category"
              placeholder="e.g. Pizza"
              value={category}
              onChange={handleCategoryNameChange}
            />
          </FormGroup>
          <FormGroup>
            <Label for="categoryIdInput">Category ID (System Code)</Label>
            <Input
              id="categoryIdInput"
              name="categoryId"
              placeholder="e.g. pizza"
              value={catId}
              onChange={(e) => setCatId(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))}
              disabled={!!editingId}
              required
            />
            <small className="text-muted">
              Must be unique. Alphanumeric and hyphens only. Used for referencing in system logic.
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="categoryStatus">Status</Label>
            <Input
              id="categoryStatus"
              type="select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active (shown in customer app)</option>
              <option value="inactive">Inactive (hidden from customer app)</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="categoryDisplayOrder">Display Order</Label>
            <Input
              id="categoryDisplayOrder"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
            />
            <small className="text-muted">
              Lower numbers appear first in the customer app.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {buttonText}
          </Button>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this category? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Category;
